"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { Truck, CheckCircle, TrendingUp, Package, ArrowRight, Zap, Users } from "lucide-react";
import ReviewSection from "@/components/landing/ReviewSection";
import TrustBadgesSection from "@/components/landing/TrustBadgesSection";

const benefits = [
  { icon: CheckCircle, title: "Access Verified Manufacturers", desc: "Connect directly with certified manufacturers offering quality solar panels, inverters, and batteries — no unverified vendors." },
  { icon: TrendingUp, title: "Expand Your Product Range", desc: "Source diverse products from multiple manufacturers and offer your customers more choices with competitive wholesale pricing." },
  { icon: Users, title: "Reach More Customers", desc: "Get discovered by homeowners, businesses, and retailers actively searching for solar solutions in your coverage area." },
  { icon: Package, title: "Streamline Operations", desc: "Manage suppliers, inventory, orders, and customer quotes — all from a single distributor dashboard built for scale." },
];

const steps = [
  { n: "01", title: "Register as Distributor", desc: "Create your account, complete your business profile, and verify your distribution credentials." },
  { n: "02", title: "Connect with Manufacturers", desc: "Browse verified manufacturers, request catalogues, and negotiate wholesale pricing directly." },
  { n: "03", title: "List & Manage Inventory", desc: "Upload your product stock, set competitive prices, and update availability in real-time." },
  { n: "04", title: "Fulfil Orders & Grow", desc: "Receive customer orders, coordinate logistics, and build your reputation with verified reviews." },
];

export default function ForDistributorsPage() {
  return (
    <div>
      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="bg-[#002301] pt-28 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <svg width="100%" height="100%"><defs><pattern id="dist-dots" width="30" height="30" patternUnits="userSpaceOnUse"><circle cx="1.5" cy="1.5" r="1.5" fill="#17BC2E" /></pattern></defs><rect width="100%" height="100%" fill="url(#dist-dots)" /></svg>
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFB600]/8 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#17BC2E]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <Truck className="w-3.5 h-3.5 text-[#FFB600]" />
              <span className="text-white/80 text-xs font-medium">For Distributors</span>
            </span>
            <h1 className="text-white font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight mb-5">
              Scale Your Solar<br /><span className="text-[#FFB600]">Distribution Network</span>
            </h1>
            <p className="text-white/65 text-lg leading-relaxed max-w-2xl mx-auto mb-10">
              Connect with verified manufacturers, list your inventory, and reach thousands of buyers across Nigeria, from a single distribution dashboard.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link href="/auth/register" className="inline-flex items-center justify-center gap-2 bg-[#FFB600] text-[#002301] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-[#17BC2E] hover:text-white transition-all duration-300 shadow-lg">
                Join as Distributor <ArrowRight className="w-4 h-4" />
              </Link>
              <Link href="/marketplace" className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/25 text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-white/20 transition-all duration-300 backdrop-blur-sm">
                View Marketplace
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── STATS ────────────────────────────────────────── */}
      <section className="bg-white border-b border-gray-100 py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { value: "200+", label: "Verified Manufacturers" },
            { value: "500+", label: "Distribution Partners" },
            { value: "36", label: "States Covered" },
            { value: "₦0", label: "Platform Fee" },
          ].map(({ value, label }) => (
            <motion.div key={label} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="text-[#002301] font-bold text-3xl">{value}</p>
              <p className="text-gray-500 text-sm mt-1">{label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── BENEFITS ─────────────────────────────────────── */}
      <section className="bg-[#f4f6f6] py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <motion.p initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-sm font-medium text-[#17BC2E] uppercase tracking-widest mb-3">Why Ren Power Group</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="text-[#002301] text-3xl sm:text-4xl font-bold">Built for Distributors</motion.h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {benefits.map(({ icon: Icon, title, desc }, i) => (
              <motion.div key={title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl p-7 border border-gray-100 shadow-sm hover:shadow-md hover:border-[#17BC2E]/30 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-[#002301]/5 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-[#FFB600]" />
                </div>
                <h3 className="text-[#002301] font-bold text-lg mb-2">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────── */}
      <section className="bg-[#002301] py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-5">
          <svg width="100%" height="100%"><defs><pattern id="dist-grid" width="40" height="40" patternUnits="userSpaceOnUse"><path d="M40 0L0 0 0 40" fill="none" stroke="#17BC2E" strokeWidth="0.8" /></pattern></defs><rect width="100%" height="100%" fill="url(#dist-grid)" /></svg>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <motion.p initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-sm font-medium text-[#FFB600] uppercase tracking-widest mb-3">Simple Process</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="text-white text-3xl sm:text-4xl font-bold">How It Works</motion.h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map(({ n, title, desc }, i) => (
              <motion.div key={n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors duration-300">
                <p className="text-[#FFB600] text-2xl font-bold mb-3">{n}</p>
                <h3 className="text-white font-semibold text-base mb-2">{title}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ReviewSection />

      {/* ── FINAL CTA ────────────────────────────────────── */}
      <section className="bg-[#f4f6f6] py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Zap className="w-10 h-10 text-[#FFB600] mx-auto mb-4" />
            <h2 className="text-[#002301] text-3xl sm:text-4xl font-bold mb-4">
              Ready to <span className="text-[#FFB600]">Grow Your Distribution?</span>
            </h2>
            <p className="text-gray-500 text-base mb-8 max-w-xl mx-auto">
              Join leading distributors already expanding their networks across Nigeria on Ren Power Group.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link href="/auth/register" className="inline-flex items-center justify-center gap-2 bg-[#002301] text-white px-8 py-4 rounded-full font-semibold hover:bg-black hover:scale-105 transition-all duration-300">
                Create Free Account <ArrowRight className="w-4 h-4" />
              </Link>
              <Link href="/contact" className="inline-flex items-center justify-center gap-2 bg-white border border-gray-200 text-[#002301] px-8 py-4 rounded-full font-semibold hover:bg-gray-50 transition-all duration-300">
                Talk to Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── TRUST BADGES ─────────────────────────────────── */}
      <TrustBadgesSection />
    </div>
  );
}
